import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  getStoreSocialProfiles,
  updateStoreSocialProfiles,
  type StoreSocialProfilesSettings,
  type UpdateStoreSocialProfilesInput,
} from '../services/store-social-profiles-admin.service';
import type { SupabaseClient } from '../supabase/client';

const CLIENT_SCOPES = new WeakMap<SupabaseClient, number>();
let nextClientScope = 0;

function clientScope(client: SupabaseClient): number {
  const existing = CLIENT_SCOPES.get(client);
  if (existing !== undefined) return existing;
  const scope = nextClientScope++;
  CLIENT_SCOPES.set(client, scope);
  return scope;
}

export const storeSocialProfilesAdminKeys = {
  all: ['store-social-profiles-admin'] as const,
  settings: (client: SupabaseClient, storeId: string) =>
    ['store-social-profiles-admin', clientScope(client), storeId.trim().toLowerCase()] as const,
};

export function useStoreSocialProfiles(
  client: SupabaseClient,
  input: {
    readonly storeId: string | null;
    readonly enabled?: boolean;
  },
): UseQueryResult<StoreSocialProfilesSettings> {
  const storeId = input.storeId?.trim() ?? '';
  return useQuery({
    queryKey: storeSocialProfilesAdminKeys.settings(client, storeId || 'pending'),
    enabled: input.enabled !== false && storeId !== '',
    queryFn: () => getStoreSocialProfiles(client, storeId),
    staleTime: 30_000,
  });
}

export function useUpdateStoreSocialProfiles(
  client: SupabaseClient,
): UseMutationResult<StoreSocialProfilesSettings, unknown, UpdateStoreSocialProfilesInput> {
  const queryClient = useQueryClient();
  return useMutation<StoreSocialProfilesSettings, unknown, UpdateStoreSocialProfilesInput, unknown>(
    {
      mutationFn: (input) => updateStoreSocialProfiles(client, input),
      onMutate: (input) =>
        queryClient.getQueryCache().find({
          queryKey: storeSocialProfilesAdminKeys.settings(client, input.storeId),
          exact: true,
        }),
      onSuccess: (settings, input, originatingQuery) => {
        const queryKey = storeSocialProfilesAdminKeys.settings(client, input.storeId);
        // Role/session changes can replace a query at the same client/store key.
        // A late save may only affect the genuine query captured before its RPC.
        const currentQuery = queryClient.getQueryCache().find({ queryKey, exact: true });
        if (originatingQuery === undefined || currentQuery !== originatingQuery) return;
        queryClient.setQueryData(queryKey, settings);
        return queryClient.invalidateQueries({
          queryKey,
          exact: true,
        });
      },
    },
  );
}
